import React, { useMemo } from "react";

import BaseDropdown from "components/form/BaseDropdown";
import { useTranslation } from "react-i18next";
import Context from "../Context";

interface AttributeDropdownProps {
  id: number;
  combinator: string;
  attribute?: string;
  disabled?: boolean;
  isInvalid?: boolean;
}

const AttributeDropdown = ({
  id,
  combinator,
  attribute,
  disabled,
  isInvalid,
}: AttributeDropdownProps): JSX.Element => {
  const context = React.useContext(Context);

  const { t } = useTranslation("queryBuilder");
  const { dispatchAction, attributes, operators, labels } = context;

  const values = useMemo(() => {
    return Object.keys(attributes)
      .filter(
        (key) =>
          // if there is at least one operation for the attribute type show it in the dropdown
          attributes[key].type === "distance" ||
          (operators[attributes[key].type] &&
            operators[attributes[key].type].filter(
              (o) =>
                o.combinators &&
                o.combinators.filter((c) => c === combinator).length > 0
            ).length > 0)
      )
      .map((key) => ({
        id: key,
        label: attributes[key].label,
        selected: key === attribute,
      }));
  }, [attributes, operators, combinator, attribute]);

  const currentType = attribute != null ? attributes[attribute]?.type : null;
  return (
    <BaseDropdown
      singleSelectionToggle={false}
      label={labels.rule.attribute.label}
      placeholder={labels.rule.attribute.placeholder}
      values={values}
      disabled={disabled}
      status={isInvalid ? "invalid" : "valid"}
      statusMessage={t("validation.attributeExists")}
      onChange={(selected) => {
        if (selected && !Array.isArray(selected)) {
          const attributeId = selected.id as string;

          const { type } = attributes[attributeId];
          const typeOperators = operators[type];

          let operator;
          if (currentType === type) {
            operator = undefined;
          } else if (typeOperators?.length === 1) {
            operator = typeOperators[0].operator;
          } else {
            operator = null;
          }

          // default boolean attributes to true
          const value = type === "boolean" ? true : undefined;

          dispatchAction({
            type: "set-attribute",
            id,
            attribute: attributeId,
            operator,
            value,
          });
        } else {
          dispatchAction({ type: "set-attribute", id, attribute: null });
        }
      }}
    />
  );
};

export default React.memo(AttributeDropdown);
